import { useMemo } from "react";
import {
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button,
  Box,
} from "@mui/material";
import { useLiveQuery } from "dexie-react-hooks";
import { Bundle, db } from "../../database/db";
import { useSettings } from "../../contexts/SettingsContext";
import CalendarViewDayIcon from "@mui/icons-material/CalendarViewDay";
import { formatCurrency } from "../../utils/formatters";

interface DailySales {
  date: string;
  totalSales: number;
  totalRevenue: number;
  averageRevenue: number;
}

interface Props {
  onViewAllSales: (date: Date) => void;
}

export default function SalesByDateTab({ onViewAllSales }: Props) {
  const { settings } = useSettings();

  const sales = useLiveQuery(() => db.sales.orderBy("timestamp").toArray());

  const dailySales: DailySales[] = useMemo(() => {
    if (!sales) return [];

    const salesByDate = sales.reduce(
      (acc: { [key: string]: DailySales }, sale) => {
        const date = new Date(sale.timestamp).toISOString().split("T")[0];

        if (!acc[date]) {
          acc[date] = {
            date,
            totalSales: 0,
            totalRevenue: 0,
            averageRevenue: 0,
          };
        }

        acc[date].totalSales++;

        const parsedProducts = JSON.parse(sale.products);
        const parsedBundles = JSON.parse(sale.bundles);

        const productTotal = parsedProducts.reduce(
          (sum: number, item: { quantity: number; priceAtTime: number }) =>
            sum + item.quantity * item.priceAtTime,
          0
        );

        const bundleDiscounts = parsedBundles.reduce(
          (sum: number, bundle: Bundle) => sum + bundle.discountAmount,
          0
        );

        const saleTotal = sale.salePrice ?? productTotal - bundleDiscounts;
        acc[date].totalRevenue += saleTotal;
        acc[date].averageRevenue =
          acc[date].totalRevenue / acc[date].totalSales;

        return acc;
      },
      {}
    );

    return Object.values(salesByDate).sort((a, b) =>
      b.date.localeCompare(a.date)
    );
  }, [sales]);

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell align="right">Total Sales</TableCell>
              <TableCell align="right">Total Revenue</TableCell>
              <TableCell align="right">Average Sale</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dailySales.map((day) => (
              <TableRow key={day.date}>
                <TableCell component="th" scope="row">
                  {formatDate(day.date)}
                </TableCell>
                <TableCell align="right">{day.totalSales}</TableCell>
                <TableCell align="right">
                  {formatCurrency(day.totalRevenue, settings)}
                </TableCell>
                <TableCell align="right">
                  {formatCurrency(day.averageRevenue, settings)}
                </TableCell>
                <TableCell align="center">
                  <Button
                    size="small"
                    startIcon={<CalendarViewDayIcon />}
                    onClick={() => onViewAllSales(new Date(day.date))}>
                    View Sales
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {dailySales.length === 0 && (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Typography color="text.secondary">
            No sales data available
          </Typography>
        </Box>
      )}
    </>
  );
}
